// Общие компоненты интерфейса обеих систем.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type Ref,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { errorText } from "./api";

// ------------------------------------------------------------------ загрузка данных

export type Async<T> = { data: T | undefined; error: string | null; loading: boolean; reload: () => void; set: (v: T) => void };

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): Async<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const run = useCallback(() => {
    const n = ++seq.current;
    setLoading(true);
    fn()
      .then((d) => {
        if (n === seq.current) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => n === seq.current && setError(errorText(e)))
      .finally(() => n === seq.current && setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(run, [run]);
  return { data, error, loading, reload: run, set: setData };
}

// ------------------------------------------------------------------ уведомления

type Toast = { id: number; text: string; err?: boolean };
const ToastCtx = createContext<(text: string, err?: boolean) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, err = false) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, err }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), err ? 7000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast${t.err ? " err" : ""}`}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastCtx);
  return {
    ok: (t: string) => push(t),
    error: (e: unknown) => push(errorText(e), true),
  };
}

// ------------------------------------------------------------------ элементы

export function Button({ variant, size, block, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "danger" | "ghost";
  size?: "small" | "big";
  block?: boolean;
}) {
  // защита от повторного нажатия (встреча 24.10.2026: два нажатия «Сохранить» создали два корпуса):
  // пока действие кнопки выполняется — запрос ушёл, ответа ещё нет — кнопка неактивна
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const { onClick, disabled, ...other } = rest;
  const cls = ["btn", variant, size, block ? "block" : "", rest.className].filter(Boolean).join(" ");
  return (
    <button type="button" {...other} className={cls} disabled={disabled || pending} aria-busy={pending || undefined}
      onClick={onClick ? (e) => {
        if (pending) return;
        const r = onClick(e) as unknown;
        if (r && typeof (r as Promise<unknown>).then === "function") {
          setPending(true);
          void (r as Promise<unknown>).catch(() => undefined).finally(() => { if (alive.current) setPending(false); });
        }
      } : undefined}>
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children, wide }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; wide?: boolean }) {
  return (
    <div className={`field${wide ? " wide" : ""}`}>
      <label>{label}</label>
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input {...props} className={`input ${props.className || ""}`} />;
}

export function Select({ options, placeholder, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & {
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select {...rest} className={`select ${rest.className || ""}`}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function Drawer({ title, onClose, children, footer, narrow, extra }: {
  title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; narrow?: boolean; extra?: ReactNode;
}) {
  useEscape(onClose);
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <aside className={`drawer${narrow ? " narrow" : ""}`} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}>
        <header>
          <h2 className="grow">{title}</h2>
          {extra}
          <Button variant="ghost" className="icon-btn" aria-label="Закрыть" onClick={onClose}>✕</Button>
        </header>
        <div className="body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </aside>
    </>
  );
}

export function Modal({ title, onClose, children, actions }: { title: string; onClose: () => void; children: ReactNode; actions: ReactNode }) {
  useEscape(onClose);
  return (
    <>
      <div className="overlay" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
        <div className="row" style={{ justifyContent: "flex-end" }}>{actions}</div>
      </div>
    </>
  );
}

/** Подтверждение необратимых действий с обязательной причиной. */
export function ReasonDialog({ title, action, onCancel, onConfirm, danger, defaultReason = "" }: {
  title: string; action: string; onCancel: () => void; onConfirm: (reason: string) => Promise<void> | void; danger?: boolean; defaultReason?: string;
}) {
  const [reason, setReason] = useState(defaultReason);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onCancel} actions={<>
      <Button onClick={onCancel}>Не сейчас</Button>
      <Button variant={danger ? "danger" : "primary"} disabled={reason.trim().length < 2 || busy}
        onClick={async () => { setBusy(true); try { await onConfirm(reason.trim()); } finally { setBusy(false); } }}>{action}</Button>
    </>}>
      <Field label="Причина" hint="Сохранится в истории изменений">
        <textarea className="input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

// Escape закрывает только верхнее окно: модальное окно поверх карточки не закрывает и карточку
const escapeStack: { current: () => void }[] = [];
let escapeBound = false;

export function useEscape(fn: () => void) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!escapeBound) {
      escapeBound = true;
      window.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && escapeStack.length) escapeStack[escapeStack.length - 1].current();
      });
    }
    escapeStack.push(ref);
    return () => {
      const i = escapeStack.lastIndexOf(ref);
      if (i >= 0) escapeStack.splice(i, 1);
    };
  }, []);
}

export function Tabs<T extends string>({ value, onChange, items }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode }[] }) {
  return (
    <div className="tabs" role="tablist">
      {items.map((i) => (
        <button key={i.value} role="tab" aria-selected={value === i.value} onClick={() => onChange(i.value)}>{i.label}</button>
      ))}
    </div>
  );
}

export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="empty"><p>{children}</p>{action && <div style={{ marginTop: 12 }}>{action}</div>}</div>;
}

export function Loading({ error, onRetry }: { error?: string | null; onRetry?: () => void }) {
  if (error) {
    return (
      <div className="alert risk" role="alert">
        <div className="t">Не удалось загрузить данные</div>
        <div className="d">{error}</div>
        {onRetry && <Button size="small" onClick={onRetry} style={{ marginTop: 8 }}>Повторить</Button>}
      </div>
    );
  }
  return <div className="row" style={{ padding: 20 }}><div className="spinner" aria-label="Загрузка" /></div>;
}

export function Kpi({ k, v, s, tone }: { k: string; v: ReactNode; s?: ReactNode; tone?: "warn" | "bad" }) {
  return (
    <div className={`kpi${tone ? " " + tone : ""}`}>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      {s && <div className="s">{s}</div>}
    </div>
  );
}

export function Bar({ value, max = 100, warn }: { value: number; max?: number; warn?: boolean }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return <div className={`bar${warn ? " warn" : ""}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}><i style={{ width: `${pct}%` }} /></div>;
}

export function Legend({ items }: { items: { cls: string; label: string }[] }) {
  return (
    <div className="legend">
      {items.map((i) => (
        <span key={i.label}><i className={i.cls} />{i.label}</span>
      ))}
    </div>
  );
}

export function NetBar({ online, queued }: { online: boolean; queued?: number }) {
  if (!online) return <div className="netbar" role="alert">Нет связи с сервером. Изменения не сохраняются, данные могут быть устаревшими.</div>;
  if (queued) return <div className="netbar queued">Ждут отправки: {queued}. Уйдут автоматически.</div>;
  return null;
}

// ------------------------------------------------------------------ номерной знак

/** Госномер как на настоящем знаке: «А 123 ВС» + регион. */
export function Plate({ value, small }: { value: string | null | undefined; small?: boolean }) {
  if (!value) return <span className="muted">машина не назначена</span>;
  const clean = value.replace(/\s+/g, "").toUpperCase();
  const m = clean.match(/^([А-ЯA-Z])(\d{3})([А-ЯA-Z]{2})(\d{2,3})$/);
  if (!m) return <span className={`plate${small ? " small" : ""}`}><span className="main">{value}</span></span>;
  return (
    <span className={`plate${small ? " small" : ""}`} aria-label={`Госномер ${value}`}>
      <span className="main">
        <small>{m[1]}</small>
        <span>{m[2]}</span>
        <small>{m[3]}</small>
      </span>
      <span className="reg">
        {m[4]}
        {!small && <span className="rus">RUS <span className="flag" /></span>}
      </span>
    </span>
  );
}

// ------------------------------------------------------------------ иконки (встроенные, без внешних наборов)

const paths: Record<string, string> = {
  schedule: "M4 6h16M4 12h10M4 18h7M17 15l3 3-3 3",
  week: "M4 5h16v15H4zM4 10h16M9 5v15M15 5v15",
  plus: "M12 5v14M5 12h14",
  operator: "M3 17h3l2-6h8l2 6h3M7 17a2 2 0 1 0 4 0M13 17a2 2 0 1 0 4 0M9 11V7h6v4",
  fleet: "M2 16V7h11v9M13 10h4l4 4v2h-8M5 18.5a1.5 1.5 0 1 0 0.01 0M17 18.5a1.5 1.5 0 1 0 0.01 0M4 16h1M8 16h5",
  shift: "M4 12a8 8 0 1 0 3-6.2M4 4v4h4M12 8v4l3 2",
  plan: "M5 20V10M10 20V4M15 20v-7M20 20V8",
  dashboard: "M4 13h6v7H4zM14 4h6v16h-6zM4 4h6v6H4z",
  report: "M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6",
  catalog: "M4 5h16M4 10h16M4 15h10M4 20h7",
  admin: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM3 12h3M18 12h3M12 3v3M12 18v3",
  building: "M5 21V4h10v17M15 9h4v12M8 8h4M8 12h4M8 16h4",
  grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  crew: "M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM16 11a3 3 0 1 0 0-6M3 20c0-3 2.5-5 5-5s5 2 5 5M14 15c2.5 0 5 2 5 5",
  issue: "M12 4l9 16H3zM12 10v4M12 17v.5",
  history: "M4 12a8 8 0 1 0 8-8M4 4v4h4M12 8v5h4",
  template: "M4 4h16v16H4zM4 9h16M9 9v11",
  projects: "M3 7h7l2 2h9v11H3z",
  logout: "M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10",
  photo: "M4 7h4l2-3h4l2 3h4v13H4zM12 10a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z",
  install: "M12 4v11M7 10l5 5 5-5M5 20h14",
  stock: "M4 20h16M6 20V11l6-5 6 5v9M9 20v-5h6v5",
  lab: "M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3M7.5 15h9",
};

export function Icon({ name, className = "ico" }: { name: keyof typeof paths | string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] || paths.catalog} />
    </svg>
  );
}

export function useInterval(fn: () => void, ms: number) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const t = window.setInterval(() => ref.current(), ms);
    return () => window.clearInterval(t);
  }, [ms]);
}
